import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { timekeepingApi } from "../../services/timekeepingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { RefreshCw, Upload, PlugZap, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

const FIXTURE_MONTHS = ["2026-04", "2026-05", "2026-06"];

export default function TimekeepingImportsPage() {
  const { t } = useLocalization();
  const { canManageTimekeeping, canTestDriveConnection } = usePermissions();
  const [flags, setFlags] = useState(null);
  const [month, setMonth] = useState("2026-06");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [connectionResult, setConnectionResult] = useState(null);

  const load = () => {
    timekeepingApi.listFiles(month).then((r) => setFiles(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!canManageTimekeeping) return;
    timekeepingApi.getFeatureFlags().then((r) => setFlags(r.data)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageTimekeeping, month]);

  if (!canManageTimekeeping) return <p className="text-sm text-gray-500" data-testid="timekeeping-imports-access-denied">{t("common.accessDenied")}</p>;

  const runFixture = async (m) => {
    setLoading(true);
    try {
      await timekeepingApi.runFixtureDiscovery(m);
      toast.success(`${t("mgmtTimekeeping.discoveryCompleteFor")} ${m}`);
      if (m === month) {
        load(); // already on this month - useEffect won't refire on an unchanged value
      } else {
        setMonth(m); // triggers the useEffect([month]) refetch for the new month
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("mgmtTimekeeping.discoveryFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      await timekeepingApi.uploadZip(file, month);
      toast.success(t("mgmtTimekeeping.zipUploaded"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("mgmtTimekeeping.uploadFailed"));
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const testConnection = async () => {
    const r = await timekeepingApi.testDriveConnection();
    setConnectionResult(r.data);
  };

  const extract = async (fileId) => {
    setLoading(true);
    try {
      await timekeepingApi.extractFile(fileId);
      toast.success(t("mgmtTimekeeping.extractionComplete"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("mgmtTimekeeping.extractionFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="timekeeping-imports-page">
      <PageHelpBanner pageKey="timekeeping-imports" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtTimekeeping.importsHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtTimekeeping.importsTitle")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtTimekeeping.importsSubtitle")}</p>
      </div>

      {flags && (
        <div className="flex flex-wrap gap-2 text-xs" data-testid="timekeeping-feature-flags">
          <span className={`px-2.5 py-1 rounded-full border font-medium ${flags.google_drive_time_card_enabled ? "bg-green-50 border-green-200 text-green-700" : "bg-gray-50 border-gray-200 text-gray-500"}`}>
            {t("mgmtTimekeeping.googleDrive")}: {flags.google_drive_time_card_enabled ? t("mgmtTimekeeping.enabled") : t("mgmtTimekeeping.disabled")}
          </span>
          <span className={`px-2.5 py-1 rounded-full border font-medium ${flags.time_card_import_enabled ? "bg-green-50 border-green-200 text-green-700" : "bg-gray-50 border-gray-200 text-gray-500"}`}>
            {t("mgmtTimekeeping.import")}: {flags.time_card_import_enabled ? t("mgmtTimekeeping.enabled") : t("mgmtTimekeeping.disabled")}
          </span>
          <span className={`px-2.5 py-1 rounded-full border font-medium ${flags.time_card_ai_extraction_enabled ? "bg-green-50 border-green-200 text-green-700" : "bg-gray-50 border-gray-200 text-gray-500"}`}>
            {t("mgmtTimekeeping.aiExtraction")}: {flags.time_card_ai_extraction_enabled ? t("mgmtTimekeeping.enabled") : t("mgmtTimekeeping.disabled")}
          </span>
        </div>
      )}

      {canTestDriveConnection && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-800">{t("mgmtTimekeeping.driveConnectionTitle")}</p>
            <p className="text-xs text-gray-500">{t("mgmtTimekeeping.driveConnectionSubtitle")}</p>
            {connectionResult && (
              <p className="text-xs mt-1" data-testid="drive-connection-result">
                <span className="font-semibold">{connectionResult.status}</span> - {connectionResult.detail}
              </p>
            )}
          </div>
          <button data-testid="test-drive-connection-btn" onClick={testConnection} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-700">
            <PlugZap size={14} /> {t("mgmtTimekeeping.testConnection")}
          </button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm font-medium text-gray-600">{t("mgmtTimekeeping.payrollMonthLabel")}</label>
          <input data-testid="month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-32" />
          <button data-testid="refresh-files-btn" onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 hover:bg-gray-50">
            <RefreshCw size={13} /> {t("mgmtTimekeeping.refresh")}
          </button>
          <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 cursor-pointer">
            <Upload size={13} /> {t("mgmtTimekeeping.uploadZip")}
            <input type="file" accept=".zip" data-testid="zip-upload-input" className="hidden" onChange={handleZipUpload} />
          </label>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">{t("mgmtTimekeeping.runFixtureLabel")}</span>
          {FIXTURE_MONTHS.map((m) => (
            <button key={m} data-testid={`run-fixture-${m}`} disabled={loading} onClick={() => runFixture(m)} className="px-3 py-1 rounded-full text-xs font-semibold border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtTimekeeping.colScan")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtAvailability.colMonth")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtTimekeeping.colSide")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtTimekeeping.colEmployeeProposed")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtTimekeeping.colMatch")}</th>
              <th className="px-4 py-3 font-medium">{t("common.status")}</th>
              <th className="px-4 py-3 font-medium">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id} className="border-t border-gray-100" data-testid={`file-row-${f.id}`}>
                <td className="px-4 py-2">
                  <button onClick={() => setPreviewFile(f)} data-testid={`preview-file-${f.id}`} className="flex items-center gap-2 text-blue-600 hover:underline text-xs">
                    <ImageIcon size={14} /> {f.original_filename}
                  </button>
                </td>
                <td className="px-4 py-2 text-xs text-gray-500">{f.payroll_month || "?"} <span className="text-gray-300">({f.month_confidence})</span></td>
                <td className="px-4 py-2"><StatusBadge status={f.proposed_side} /></td>
                <td className="px-4 py-2 text-xs text-gray-700">{f.proposed_employee_name || "-"}</td>
                <td className="px-4 py-2"><StatusBadge status={f.employee_match_status} /></td>
                <td className="px-4 py-2"><StatusBadge status={f.processing_status} /></td>
                <td className="px-4 py-2">
                  {f.processing_status !== "EXTRACTED" && (
                    <button data-testid={`extract-btn-${f.id}`} disabled={loading} onClick={() => extract(f.id)} className="px-2.5 py-1 rounded text-xs font-semibold bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50">
                      {t("mgmtTimekeeping.runAiExtraction")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {files.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500" data-testid="no-files-row">{t("mgmtTimekeeping.noFiles")}</td></tr>}
          </tbody>
        </table>
      </div>

      <Link to="/management/timekeeping/monthly" data-testid="go-to-monthly-dashboard-link" className="inline-block text-sm text-blue-600 hover:underline">
        {t("mgmtTimekeeping.goToMonthlyDashboard")} &rarr;
      </Link>

      {previewFile && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-6" onClick={() => setPreviewFile(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-4" onClick={(e) => e.stopPropagation()} data-testid="file-preview-modal">
            <div className="flex justify-between items-center mb-2">
              <p className="font-semibold text-sm">{previewFile.original_filename}</p>
              <button onClick={() => setPreviewFile(null)} data-testid="close-preview-modal"><X size={18} /></button>
            </div>
            <img src={timekeepingApi.getFileImageUrl(previewFile.id)} alt={previewFile.original_filename} className="w-full rounded-lg border border-gray-100" />
            {previewFile.extraction && (
              <pre className="text-xs bg-gray-50 rounded-lg p-3 mt-3 overflow-auto max-h-64" data-testid="extraction-json">
                {JSON.stringify(previewFile.extraction, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
